import '@fontsource-variable/geist';
import './styles/global.css';
import './styles/tailwind.css';

import { DatesProvider } from '@mantine/dates';
import { Notifications, notifications } from '@mantine/notifications';
import { ErrorBoundary } from '@sentry/react';
import {
  MutationCache,
  type Query,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';
import { TooltipProvider } from 'design-system/tooltip/tooltip';
import { env } from 'env';
import { invalidateSourceQueries } from 'features/sources/lib/invalidate-source-queries';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { StrictMode, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { createBrowserRouter, RouterProvider, useLocation } from 'react-router';

import { ErrorFallback } from '@/common/error-fallback';
import SetUsers from '@/common/set-user';

import { AgentPanelWrapper } from './components/agent/agent-panel-wrapper';
import { MobileNavDrawer } from './components/common/mobile-nav-drawer';
import { SidebarUIProvider } from './components/common/sidebar-context';
import { AuthProvider } from './lib/auth';
import { FeatureFlagProvider } from './lib/feature-flags';
import { GlobalStyles } from './lib/global-styles';
import { KAPA_BRANDING, KapaAgentProvider } from './lib/kapa-agent-provider';
import { POSTHOG_PROXY_PATH, POSTHOG_UI_HOST } from './lib/posthog-proxy';
import { ThemeProvider } from './lib/theme-provider';
import { useSchemeExperiment } from './lib/use-scheme-experiment';
import AppRoutes from './routes';

// Track pageviews on route changes
const PostHogPageviewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    posthog?.capture('$pageview');
  }, [location.pathname]);

  return null;
};

// Check that PostHog is client-side
if (typeof window !== 'undefined' && env.VITE_POSTHOG_KEY) {
  posthog.init(env.VITE_POSTHOG_KEY, {
    api_host: POSTHOG_PROXY_PATH,
    ui_host: POSTHOG_UI_HOST,
    // Anonymous traffic would otherwise create a person profile per visitor.
    person_profiles: 'identified_only',
    // Enable debug mode in development
    loaded: (posthog) => {
      if (import.meta.env.DEV) posthog.debug();
    },
  });
}

// QueryCache.onError fires for every error event (each retry attempt plus the
// terminal one). Toast only on terminal failure so polling pages don't spam.
// Handles all retry shapes v5 accepts: number, false, true (infinite), and
// the (failureCount, error) => boolean function form.
const isStillRetrying = (
  query: Query<unknown, unknown, unknown, readonly unknown[]>,
): boolean => {
  const retry = query.options.retry;
  const failureCount = query.state.fetchFailureCount;
  if (retry === false) return false;
  if (retry === true) return true;
  if (typeof retry === 'number') return failureCount <= retry;
  if (typeof retry === 'function') {
    return retry(failureCount, query.state.error as Error);
  }
  return failureCount <= 3;
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (_error, query) => {
      if (isStillRetrying(query)) return;
      const meta = query.meta;
      if (!meta?.errorMessage) return;
      notifications.show({
        title: meta.errorTitle ?? 'Error',
        message: meta.errorMessage,
        color: 'red',
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (_error, _vars, _ctx, mutation) => {
      const meta = mutation.meta;
      if (!meta?.errorMessage) return;
      notifications.show({
        title: meta.errorTitle ?? 'Error',
        message: meta.errorMessage,
        color: 'red',
      });
    },
  }),
});

const RouterApplication = () => (
  <AuthProvider>
    <DatesProvider settings={{}}>
      <PostHogProvider client={posthog}>
        <FeatureFlagProvider>
          <KapaAgentProvider
            onSourceChanged={(sourceId) =>
              void invalidateSourceQueries(queryClient, sourceId)
            }
          >
            <PostHogPageviewTracker />
            <SetUsers />
            <Notifications />
            <ErrorBoundary fallback={ErrorFallback}>
              <AgentPanelWrapper branding={KAPA_BRANDING} />
              <SidebarUIProvider>
                <MobileNavDrawer />
                <AppRoutes />
              </SidebarUIProvider>
            </ErrorBoundary>
            <Analytics />
          </KapaAgentProvider>
        </FeatureFlagProvider>
      </PostHogProvider>
    </DatesProvider>
  </AuthProvider>
);

const router = createBrowserRouter([
  {
    path: '*',
    element: <RouterApplication />,
  },
]);

const App = () => {
  const scheme = useSchemeExperiment();

  return (
    <StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider scheme={scheme}>
            <GlobalStyles />
            <TooltipProvider>
              <RouterProvider router={router} />
            </TooltipProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </StrictMode>
  );
};

export default App;
